/**
 * AUTH.9. What the app remembers about an old goals report link.
 *
 * Two things, kept apart because they live for different lengths of time:
 *
 *  - THE VIEW, in sessionStorage: the shared report page was opened from an
 *    old link, so it shows the "Is this your Goals Report?" banner. Per tab,
 *    and it survives a reload of that tab.
 *  - THE PENDING STEP, in localStorage: the visitor asked to sign in (or to add
 *    their email) from that banner or the expired card. The magic link lands
 *    in whatever tab the email opens, so this must be visible to every tab.
 *    It holds the token only for a claim, and lapses after a day.
 *
 * Storage can be missing or throw (private windows, blocked site data), so
 * every read and write is guarded and a failure means "nothing remembered".
 */

export interface ReturnLinkView {
  token: string;
  canClaim: boolean;
  renewed: boolean;
}

export interface ReturnLinkPending {
  mode: 'claim' | 'signin';
  token?: string;
  at: number;
}

const VIEW_KEY = 'cpd.returnLinkView';
const PENDING_KEY = 'cpd.returnLinkPending';
/** How long a "sign in, then take me to my report" request is honored. */
export const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

function read<T>(storage: () => Storage, key: string): T | null {
  try {
    const raw = storage().getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: unknown): void {
  try {
    if (value === null) {
      storage().removeItem(key);
    } else {
      storage().setItem(key, JSON.stringify(value));
    }
  } catch {
    // Nothing remembered. The page still works; only the banner or the
    // redirect after sign-in is lost.
  }
}

const session = () => window.sessionStorage;
const local = () => window.localStorage;

export const returnLinkMemory = {
  view(token: string | null): ReturnLinkView | null {
    const view = read<ReturnLinkView>(session, VIEW_KEY);
    return view && token && view.token === token ? view : null;
  },

  rememberView(view: ReturnLinkView): void {
    write(session, VIEW_KEY, view);
  },

  pending(now = Date.now()): ReturnLinkPending | null {
    const pending = read<ReturnLinkPending>(local, PENDING_KEY);

    if (!pending || now - pending.at > PENDING_TTL_MS) {
      return null;
    }

    return pending.mode === 'claim' && !pending.token ? null : pending;
  },

  rememberPending(pending: Omit<ReturnLinkPending, 'at'>, now = Date.now()): void {
    write(local, PENDING_KEY, { ...pending, at: now });
  },

  clearPending(): void {
    write(local, PENDING_KEY, null);
  }
};
